import { useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Divider,
  Grid,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "../../../../language";


// ---------------------------------------------------------------------------
// EnterotypeCard
// ---------------------------------------------------------------------------

/**
 * EnterotypeCard
 *
 * Displays the patient's gut enterotype classification with an interactive
 * explorer for all three types.
 *
 * Props:
 *   value  {number|string}  The patient's enterotype result (1, 2, or 3).
 */
export default function EnterotypeCard({ value }) {

  const theme = useTheme();
  const { t } = useTranslation();

  const initialId = Math.round(Number(value)) || 1;
  const [selectedId, setSelectedId] = useState(value);

  
// imported functions
// ---------------------------------------------------------------------------
// Static data
// ---------------------------------------------------------------------------
const ENTEROTYPES = [
  {
    id: 1,
    genus: "Bacteroides",
    color: theme.palette.tertiary.C400,
    bgColor: theme.palette.tertiary.C100,
    //textColor: "#085041",
    summary:
      t ("entero8"),
    traits: [
      t ("entero9"),
      t ("entero10"),
      t ("entero11")],
    dietTip:
      t ("entero12"),
  },
  {
    id: 2,
    genus: "Prevotella",
    color: theme.palette.primary.light,
    bgColor: theme.palette.primary.C50,
    //textColor: "#633806",
    summary:
    t ("entero14"),
    traits: [
      t ("entero15"),
      t ("entero16"),
      t ("entero17")
    ],
    dietTip:
      t ("entero18"),
  },
  {
    id: 3,
    genus: "Ruminococcus",
    color: theme.palette.quartiary.C200,
    bgColor: theme.palette.quartiary.C50,
    //textColor: "#0C447C",
    summary:
      t ("entero20"),
    traits: [
      t ("entero21"),
      t ("entero22"),
      t ("entero23")
    ],
    dietTip:
      t ("entero24"),
  },
];

// ---------------------------------------------------------------------------
// TypeButton
// ---------------------------------------------------------------------------
function TypeButton({ type, isActive, onClick }) {
  const theme = useTheme();

  return (
    <Box
      component="button"
      onClick={() => onClick(type.id)}
      sx={{
        all: "unset",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        p: { xs: 1, sm: 1.5 },
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        borderRadius: 2,
        border: isActive
          ? `2px solid ${type.color}`
          : `1px solid ${theme.palette.divider}`,
        bgcolor: isActive
          ? theme.palette.mode === "light"
            ? type.bgColor
            : `${type.color}22`
          : theme.palette.mode === "light"
          ? "grey.50"
          : "action.hover",
        transition: "border-color 0.15s, background-color 0.15s",
        "&:hover": {
          borderColor: type.color,
          bgcolor:
            theme.palette.mode === "light"
              ? type.bgColor
              : `${type.color}18`,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 }, minWidth: 0 }}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            bgcolor: type.color,
            flexShrink: 0,
          }}
        />
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 500,
            color: isActive ? type.textColor : "text.primary",
            whiteSpace: "nowrap",
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
          }}
        >
          Type {type.id}
        </Typography>
      </Box>
      <Typography
        variant="caption"
        sx={{
          color: isActive ? type.textColor : "text.primary",
          lineHeight: 1.4,
          opacity: 0.9,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          fontSize: { xs: "0.65rem", sm: "0.75rem" },
        }}
      >
        {type.genus}
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// TraitRow
// ---------------------------------------------------------------------------
function TraitRow({ color, text }) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 0.75 }}>
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          bgcolor: color,
          flexShrink: 0,
          mt: "6px",
        }}
      />
      <Typography variant="subtitle2" sx={{fontWeight:300, lineHeight: 1.6 }}>
        {text}
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// DetailPanel
// ---------------------------------------------------------------------------
function DetailPanel({ type }) {
  const theme = useTheme();

  return (
    <Box>
      <Divider sx={{ my: 1.5 }} />

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: "50%",
            bgcolor: type.color,
            flexShrink: 0,
          }}
        />
        <Typography variant="subtitle1" fontWeight={500}>
          Type {type.id} — {type.genus}
        </Typography>
      </Box>

      <Typography
        variant="subtitle2"
        sx={{ mb: 1.5, lineHeight: 1.65, fontWeight: 400 }}
      >
        {type.summary}
      </Typography>

      <Box sx={{ mb: 1.5 }}>
        {type.traits.map((trait, i) => (
          <TraitRow key={i} color={type.color} text={trait} />
        ))}
      </Box>

      <Box
        sx={{
          p: "10px 14px",
          bgcolor:
            theme.palette.mode === "light"
              ? "grey.50"
              : "action.hover",
          borderLeft: `3px solid ${type.color}`,
          borderRadius: 0,
        }}
      >
        <Typography variant="subtitle2" sx={{ lineHeight: 1.6 }}>
          <Box component="span" sx={{ fontWeight: 500, color: "text.primary" }}>
            Note:{" "}
          </Box>
          {type.dietTip}
        </Typography>
      </Box>
    </Box>
  );
}
  
const selected = ENTEROTYPES.find((t) => t.id === selectedId) ?? ENTEROTYPES[0];
  const patientType = ENTEROTYPES.find((t) => t.id === initialId) ?? ENTEROTYPES[0];


  return (
    
    <Box
      sx={{
        px: { xs: 0, sm: 0.5, md: 1.5 },
        py: { xs: 2, sm: 2.5, md: 4 },
        maxWidth: { xs: 380, sm: 400, md: 400, lg: 500, xl: 540 },
        width: "100%",
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          mb: 0.5,
        }}
      >
        <Box>
          <Typography
            variant="overline"
            sx={{
              fontSize: "0.68rem",
              letterSpacing: "0.08em",
              lineHeight: 1,
              display: "block",
              mb: 0.5,
            }}
          >
            {t ("entero2")}
          </Typography>
          <Typography variant="h6" fontWeight={500}>
            {t ("entero3")}
          </Typography>
        </Box>

        <Chip
          label={
            <Typography variant="subtitle1" fontWeight={600} sx={{ color: theme.palette.mode === "dark" ? "greydark.C900" : patientType.textColor }}>
                TYPE &nbsp;&nbsp;{value}
            </Typography>
          }
          size="medium"
          sx={{
            bgcolor: patientType.bgColor, color: theme.palette.mode === "dark" ? "greydark.C900" : patientType.textColor, border: `1px solid ${patientType.color}44`,
            px: 1,
            "& .MuiChip-label": { px: 1 },
          }}
        />
      </Box>

      <Divider sx={{ my: 1.5 }} />

      {/* ── What is an enterotype ── */}
      <Typography
        variant="overline"
        sx={{
          fontSize: "0.68rem",
          letterSpacing: "0.08em",
          display: "block",
          mb: 0.75,
        }}
      >
        {t ("entero4")}
      </Typography>
      <Typography variant="subtitle2" fontWeight={400} sx={{ lineHeight: 1.65 }}>
        {t ("entero5")}
      </Typography>

      <Divider sx={{ my: 1.5 }} />

      {/* ── Type selector ── */}
      <Typography
        variant="overline"
        sx={{
          fontSize: "0.68rem",
          letterSpacing: "0.08em",
          display: "block",
          mb: 1,
        }}
      >
        {t ("entero6")}
      </Typography>

      <Grid container spacing={{ xs: 0.5, sm: 1 }}>
        {ENTEROTYPES.map((type) => (
          <Grid key={type.id} size={4}>
            <TypeButton
              type={type}
              isActive={selectedId === type.id}
              onClick={setSelectedId}
            />
          </Grid>
        ))}
      </Grid>

      {/* ── Detail panel ── */}
      <DetailPanel type={selected} />
    </Box>
  );
}